// client/src/components/pages/Settings/LogoSettings.tsx (converted from .jsx, ST-00224)
import {
  useState,
  useEffect,
  useCallback,
  type ChangeEvent,
  type ComponentType,
  type FormEvent,
} from 'react';
import { Upload, Image as ImageIcon, Star, Trash2 } from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import BreadcrumbsJs from '../../common/Breadcrumbs';
import type { LogoApiError, LogoDataUri, LogoItem } from '../../../types/clientPages/logoSettings';
import './SettingsDetail.css';

const Breadcrumbs = BreadcrumbsJs as unknown as ComponentType<{
  items: Array<{ label: string; path?: string }>;
}>;

/**
 * Organization branding (ST-00127).
 *
 * The tenant's logo library plus its "brand logo" — the one logo shown in the
 * app chrome and preselected for documents. Every logo is previewed against a
 * light and a dark swatch so users can see how it renders in both themes
 * before they commit to it.
 *
 * ST-00224: choosing, uploading as, or deleting the brand logo needs the Edit Settings
 * permission (settings.edit; administrators hold every permission). It used to need an
 * administrator role. Adding and deleting other logos stays open to the page.
 */

/** The server's message from an axios error, else the error's own. */
const errorText = (err: unknown): string => {
  const e = err as LogoApiError;
  return e?.response?.data?.message || e?.message || '';
};

// Deliberately hard-coded: these are the two THEME BACKGROUNDS being simulated,
// not themed surfaces. They must stay fixed whichever theme the page is in.
const SWATCHES = [
  { key: 'light', label: 'Light theme', bg: '#ffffff' },
  { key: 'dark', label: 'Dark theme', bg: '#161b22' },
];

interface LogoPreviewProps {
  dataUri: string | null | undefined;
  name: string | null | undefined;
}

const LogoPreview = ({ dataUri, name }: LogoPreviewProps) => (
  <div style={{ display: 'flex', gap: '8px' }}>
    {SWATCHES.map((swatch) => (
      <div key={swatch.key} style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            background: swatch.bg,
            border: '1px solid var(--border)',
            borderRadius: '8px',
            height: '72px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
          }}
        >
          {dataUri ? (
            <img
              src={dataUri}
              alt={`${name || 'Logo'} on a ${swatch.label} background`}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <ImageIcon size={20} style={{ color: 'var(--text-faint)' }} />
          )}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: 'var(--text-muted)',
            textAlign: 'center',
            marginTop: '4px',
          }}
        >
          {swatch.label}
        </div>
      </div>
    ))}
  </div>
);

const LogoSettings = () => {
  const { tenantLogo, refreshTenantBranding, hasPermission } = useAuth();
  // ST-00224: the brand logo is an organisation setting (Edit Settings).
  const canManageBranding = hasPermission('settings.edit');

  // Library
  const [logos, setLogos] = useState<LogoItem[]>([]);
  const [logosLoading, setLogosLoading] = useState(true);
  const [thumbnails, setThumbnails] = useState<Record<number, string | null>>({});
  const [busyLogoId, setBusyLogoId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // Upload form
  const [uploadLogoName, setUploadLogoName] = useState('');
  const [uploadLogoFile, setUploadLogoFile] = useState<File | null>(null);
  const [uploadAsDefault, setUploadAsDefault] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Feedback
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const loadLogos = useCallback(async () => {
    setLogosLoading(true);
    try {
      const response = await api.logos.getAll();
      const list: LogoItem[] = Array.isArray(response.data)
        ? (response.data as unknown as LogoItem[])
        : [];
      setLogos(list);

      // /uploads is not proxied in deployment, so thumbnails must come from the
      // API as data URIs. Counts are small (a handful per tenant).
      const entries = await Promise.all(
        list.map(async (logo): Promise<[number, string | null]> => {
          try {
            const res = await api.logos.getBase64(logo.id);
            return [logo.id, (res.data as LogoDataUri | undefined)?.dataUri || null];
          } catch {
            return [logo.id, null];
          }
        })
      );
      setThumbnails(Object.fromEntries(entries));
    } catch (err) {
      console.error('Error loading logos:', err);
      setError('Failed to load logos. ' + errorText(err));
      setLogos([]);
    } finally {
      setLogosLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogos();
  }, [loadLogos]);

  // Revoke the object URL created for the pre-upload preview.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleLogoFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setUploadLogoFile(file);
    setError('');
    setSuccess('');

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const handleLogoUpload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!uploadLogoFile || !uploadLogoName.trim()) {
      setError('Please provide a logo name and select a file.');
      return;
    }

    setUploadingLogo(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('logoName', uploadLogoName.trim());
    formData.append('logoFile', uploadLogoFile);

    const setAsDefault = uploadAsDefault && canManageBranding;

    try {
      await api.logos.upload(formData, { setAsDefault });
      setSuccess(
        setAsDefault
          ? `"${uploadLogoName.trim()}" uploaded and set as the brand logo.`
          : `Logo "${uploadLogoName.trim()}" uploaded successfully.`
      );
      setUploadLogoName('');
      setUploadLogoFile(null);
      setUploadAsDefault(false);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);

      // Clear the file input
      const fileInput = document.getElementById('logoFileInput') as HTMLInputElement | null;
      if (fileInput) fileInput.value = '';

      await loadLogos();
      await refreshTenantBranding();
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('Failed to upload logo. ' + errorText(err));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSetDefault = async (logo: LogoItem) => {
    setBusyLogoId(logo.id);
    setError('');
    setSuccess('');
    try {
      await api.logos.setDefault(logo.id);
      setSuccess(`"${logo.name}" is now the brand logo.`);
      await loadLogos();
      await refreshTenantBranding();
    } catch (err) {
      console.error('Error setting brand logo:', err);
      setError('Failed to set the brand logo. ' + errorText(err));
    } finally {
      setBusyLogoId(null);
    }
  };

  const handleDelete = async (logo: LogoItem) => {
    setBusyLogoId(logo.id);
    setError('');
    setSuccess('');
    try {
      await api.logos.delete(logo.id);
      setConfirmDeleteId(null);
      setSuccess(`Logo "${logo.name}" deleted.`);
      await loadLogos();
      await refreshTenantBranding();
    } catch (err) {
      console.error('Error deleting logo:', err);
      setError('Failed to delete logo. ' + errorText(err));
    } finally {
      setBusyLogoId(null);
    }
  };

  const breadcrumbItems = [{ label: 'Organization' }, { label: 'Branding' }, { label: 'Logo' }];

  const defaultLogo = logos.find((l) => l.isDefault) || null;

  return (
    <div className="settings-detail">
      <Breadcrumbs items={breadcrumbItems} />

      <div className="settings-detail-container">
        {/* Header */}
        <div className="settings-detail-header">
          <div className="settings-detail-header-icon">
            <ImageIcon size={26} />
          </div>
          <div>
            <h1 className="settings-detail-title">Organization Branding</h1>
            <p className="settings-detail-description">
              Choose the brand logo shown across the app, and manage the logos available for
              reports, emails, and documents
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="settings-detail-content">
          {error && (
            <div className="settings-alert settings-alert-error" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="settings-alert settings-alert-success" role="status">
              {success}
            </div>
          )}

          {/* Current brand logo */}
          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              <Star size={20} />
              <h2>Brand Logo</h2>
            </div>

            {defaultLogo || tenantLogo ? (
              <div>
                <p className="settings-form-hint" style={{ marginBottom: '10px' }}>
                  <strong style={{ color: 'var(--text-strong)' }}>
                    {defaultLogo ? defaultLogo.name : tenantLogo?.name}
                  </strong>{' '}
                  is shown in the app for everyone in your organization.
                </p>
                <div style={{ maxWidth: '420px' }}>
                  <LogoPreview
                    dataUri={
                      (defaultLogo && thumbnails[defaultLogo.id]) ||
                      (tenantLogo && tenantLogo.dataUri) ||
                      null
                    }
                    name={defaultLogo ? defaultLogo.name : tenantLogo?.name}
                  />
                </div>
              </div>
            ) : (
              <p className="settings-form-hint">
                No brand logo chosen &mdash; LexiBridge default branding is shown.
                {canManageBranding
                  ? ' Pick one from the library below, or upload a new logo.'
                  : ' Someone with the Edit Settings permission can choose one from the logo library.'}
              </p>
            )}
          </div>

          {/* Logo library */}
          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              <ImageIcon size={20} />
              <h2>Logo Library</h2>
            </div>

            {logosLoading ? (
              <div className="settings-loading">
                <span className="settings-spinner"></span>
                Loading logos...
              </div>
            ) : logos.length === 0 ? (
              <p className="settings-form-hint">
                No logos uploaded yet. Use the form below to add your first one.
              </p>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: '16px',
                }}
              >
                {logos.map((logo) => {
                  const busy = busyLogoId === logo.id;
                  return (
                    <div
                      key={logo.id}
                      role="group"
                      aria-label={`${logo.name} logo`}
                      style={{
                        border: logo.isDefault
                          ? '1px solid var(--accent-border)'
                          : '1px solid var(--border)',
                        borderRadius: '10px',
                        padding: '12px',
                        background: logo.isDefault ? 'var(--accent-soft)' : 'var(--surface)',
                        opacity: busy ? 0.6 : 1,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          marginBottom: '10px',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: 'var(--text-strong)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={logo.name}
                        >
                          {logo.name}
                        </span>
                        {logo.isDefault && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              color: 'var(--accent-text)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Star size={12} fill="currentColor" />
                            Brand logo
                          </span>
                        )}
                      </div>

                      <LogoPreview dataUri={thumbnails[logo.id]} name={logo.name} />

                      {confirmDeleteId === logo.id ? (
                        <div style={{ marginTop: '12px' }}>
                          <p className="settings-form-hint" style={{ marginBottom: '8px' }}>
                            Delete &ldquo;{logo.name}&rdquo;? It will be removed from the library
                            {logo.isDefault
                              ? ' and your organization will fall back to LexiBridge branding'
                              : ''}
                            .
                          </p>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="settings-btn settings-btn-danger"
                              disabled={busy}
                              onClick={() => handleDelete(logo)}
                            >
                              <Trash2 size={16} />
                              Confirm delete
                            </button>
                            <button
                              type="button"
                              className="settings-btn settings-btn-secondary"
                              disabled={busy}
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            marginTop: '12px',
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          {canManageBranding && !logo.isDefault && (
                            <button
                              type="button"
                              className="settings-btn settings-btn-secondary"
                              disabled={busy}
                              onClick={() => handleSetDefault(logo)}
                              title="Show this logo across the app"
                            >
                              <Star size={16} />
                              Set as brand logo
                            </button>
                          )}
                          {(canManageBranding || !logo.isDefault) && (
                            <button
                              type="button"
                              className="settings-btn settings-btn-danger"
                              disabled={busy}
                              onClick={() => {
                                setConfirmDeleteId(logo.id);
                                setError('');
                                setSuccess('');
                              }}
                              title={
                                logo.isDefault
                                  ? 'Removes the brand logo — the app falls back to LexiBridge branding'
                                  : 'Remove this logo from the library'
                              }
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!logosLoading && logos.length > 0 && !canManageBranding && (
              <p className="settings-form-hint" style={{ marginTop: '12px' }}>
                Changing which logo is the brand logo needs the Edit Settings permission.
              </p>
            )}
          </div>

          {/* Upload */}
          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              <Upload size={20} />
              <h2>Upload New Logo</h2>
            </div>

            <form
              onSubmit={handleLogoUpload}
              className="settings-form"
              aria-label="Upload new logo"
            >
              <div className="settings-form-group">
                <label htmlFor="logoName" className="settings-form-label">
                  Logo Name <span className="settings-form-required">*</span>
                </label>
                <input
                  type="text"
                  id="logoName"
                  value={uploadLogoName}
                  onChange={(e) => setUploadLogoName(e.target.value)}
                  className="settings-form-input"
                  placeholder="Enter a descriptive name for the logo"
                  required
                />
                <p className="settings-form-hint">
                  This name will be used to identify the logo in the system
                </p>
              </div>

              <div className="settings-form-group">
                <label htmlFor="logoFileInput" className="settings-form-label">
                  Logo File <span className="settings-form-required">*</span>
                </label>
                <input
                  type="file"
                  id="logoFileInput"
                  onChange={handleLogoFileChange}
                  accept="image/png, image/jpeg, image/gif, image/svg+xml"
                  className="settings-form-file"
                  required
                />
                <p className="settings-form-hint">
                  Supported formats: PNG, JPG, GIF, SVG (Max 5MB)
                </p>
              </div>

              {/* Preview */}
              {previewUrl && (
                <div className="settings-form-group">
                  <label className="settings-form-label">Preview</label>
                  <div style={{ maxWidth: '420px' }}>
                    <LogoPreview dataUri={previewUrl} name={uploadLogoName || 'New logo'} />
                  </div>
                </div>
              )}

              {canManageBranding && (
                <div className="settings-form-group">
                  <label className="settings-form-checkbox">
                    <input
                      type="checkbox"
                      checked={uploadAsDefault}
                      onChange={(e) => setUploadAsDefault(e.target.checked)}
                    />
                    Set as brand logo
                  </label>
                  <p className="settings-form-hint">
                    Replaces the current brand logo everywhere in the app
                  </p>
                </div>
              )}

              <div className="settings-form-actions">
                <button
                  type="submit"
                  disabled={uploadingLogo || !uploadLogoFile || !uploadLogoName.trim()}
                  className="settings-btn settings-btn-primary"
                >
                  {uploadingLogo ? (
                    <>
                      <span className="settings-btn-spinner"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Upload Logo
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Info Card */}
          <div className="settings-info-card">
            <h3 className="settings-info-title">Logo Usage</h3>
            <ul className="settings-info-list">
              <li>The brand logo appears in the app for everyone in your organization</li>
              <li>Logos are also used in PDF reports and email headers</li>
              <li>SVG format is recommended for best quality</li>
              <li>Transparent backgrounds work best &mdash; check both previews above</li>
              <li>Recommended dimensions: 200x60 pixels (landscape)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoSettings;
